import { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SIZE_GRADE_OPTIONS } from '@/lib/sizeGrades';
import { Button } from '@/components/ui/Button';
import { parseTypedAmount } from '@/lib/number';

const MAX_PRICE_PER_KG = 999999.99;

export interface HarvestValues {
  total_weight_kg: number;
  piece_count: number | null;
  size_grade: string | null;
  price_per_kg: number | null;
}

interface HarvestDialogProps {
  open: boolean;
  harvestType: 'partial' | 'final';
  pondName?: string;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (values: HarvestValues) => void;
}

/**
 * Harvests are created with their weight in one call, never as blank placeholders:
 * a final harvest closes the cycle (#157), and technicians can't edit a harvest
 * afterwards (#156), so the values have to be right at creation — including the
 * optional size grade and price (#170).
 */
export function HarvestDialog({
  open,
  harvestType,
  pondName,
  submitting,
  onClose,
  onConfirm,
}: HarvestDialogProps) {
  const isFinal = harvestType === 'final';
  const [weight, setWeight] = useState('');
  const [pieces, setPieces] = useState('');
  const [sizeGrade, setSizeGrade] = useState('');
  const [price, setPrice] = useState('');
  const [error, setError] = useState<string>();
  const [priceError, setPriceError] = useState<string>();
  const [piecesError, setPiecesError] = useState<string>();

  // `submitting` flips a tick after onConfirm, so two clicks in one frame would both create (a duplicate final harvest closes the crop twice).
  const sendingRef = useRef(false);
  useEffect(() => {
    if (!submitting) sendingRef.current = false;
  }, [submitting]);

  const handleClose = () => {
    // A create that is queued or in flight would still land after the user thinks they cancelled.
    if (submitting) return;
    setWeight('');
    setPieces('');
    setSizeGrade('');
    setPrice('');
    setError(undefined);
    setPriceError(undefined);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (sendingRef.current) return;
    // A decimal comma ("1,5") is read as 1.5 and a bare thousands group ("1,250") is refused,
    // so nothing is silently stored 10x or 1000x off.
    const weightParsed = parseTypedAmount(weight);
    if ('error' in weightParsed) {
      setError(weightParsed.error);
      return;
    }
    const kg = weightParsed.value ?? 0;
    if (!(kg > 0)) {
      setError('Enter the total harvested weight (more than 0 kg)');
      return;
    }
    const priceParsed = parseTypedAmount(price);
    if ('error' in priceParsed) {
      setPriceError(priceParsed.error);
      return;
    }
    const perKg = priceParsed.value;
    // price_per_kg is DECIMAL(8,2); a larger value overflows in the DB and 500s.
    if (perKg !== null && !(Number.isFinite(perKg) && perKg >= 0 && perKg <= MAX_PRICE_PER_KG)) {
      setPriceError(`Enter a price from 0 to ${MAX_PRICE_PER_KG.toLocaleString()}, or leave it blank`);
      return;
    }
    const piecesParsed = parseTypedAmount(pieces, { integer: true });
    if ('error' in piecesParsed) {
      setPiecesError(piecesParsed.error);
      return;
    }
    const count = piecesParsed.value;
    sendingRef.current = true;
    onConfirm({
      total_weight_kg: kg,
      piece_count: count !== null && count >= 0 ? count : null,
      size_grade: sizeGrade || null,
      price_per_kg: perKg,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={isFinal ? 'Record final harvest' : 'Record partial harvest'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {isFinal && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <p className="text-sm text-amber-800">
              This closes the cycle{pondName ? ` for ${pondName}` : ''} and marks the pond idle.
              Record it only when the pond has been fully harvested.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Total weight (kg)"
            type="text"
            inputMode="decimal"
            value={weight}
            onChange={(e) => {
              setWeight(e.target.value);
              setError(undefined);
            }}
            error={error}
            autoFocus
          />
          <Input
            label="Piece count (optional)"
            type="text"
            inputMode="numeric"
            value={pieces}
            onChange={(e) => {
              setPieces(e.target.value);
              setPiecesError(undefined);
            }}
            error={piecesError}
          />
          <Select
            label="Size grade (optional)"
            value={sizeGrade}
            options={[{ value: '', label: 'Not set' }, ...SIZE_GRADE_OPTIONS]}
            onChange={(e) => setSizeGrade(e.target.value)}
          />
          <Input
            label="Price per kg, ₱ (optional)"
            type="text"
            inputMode="decimal"
            value={price}
            onChange={(e) => {
              setPrice(e.target.value);
              setPriceError(undefined);
            }}
            error={priceError}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant={isFinal ? 'danger' : 'primary'} loading={submitting}>
            {isFinal ? 'Close cycle' : 'Save harvest'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
